import { useState, type FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAuth } from "@/auth/useAuth"
import { ArrowLeftIcon, ArrowRightIcon } from "@/components/icons"
import { Button } from "@/components/ui-kit"
import { CustomerInfo } from "@/components/ui-kit/customer-info"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { getApiErrorMessage } from "@/lib/error-codes"
import { cn } from "@/lib/utils"

export function CustomerSelectPage({ redirectTo = "/home" }: { redirectTo?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { customers, selectCustomer, logout } = useAuth()
  const [selectedId, setSelectedId] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleConfirm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!selectedId) return

    setSubmitError(null)
    setIsSubmitting(true)

    try {
      await selectCustomer(Number(selectedId))
      navigate(redirectTo, { replace: true })
    } catch (error) {
      setSubmitError(getApiErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = async () => {
    setIsLoggingOut(true)

    try {
      await logout()
    } finally {
      setIsLoggingOut(false)
    }
  }

  return (
    <>
      <Button
        variant="link"
        type="button"
        className="mb-4 w-fit px-0 py-0 text-base text-muted-foreground"
        onClick={() => void handleBack()}
        disabled={isLoggingOut || isSubmitting}
      >
        <span className="inline-flex items-center gap-2">
          <span aria-hidden>
            <ArrowLeftIcon size={18} />
          </span>
          <span className="leading-[1]">{t("customerSelect.backToLogin")}</span>
        </span>
      </Button>

      <h1 className="sm:mb-4 mb-3 text-[28px] leading-[36px] font-bold text-primary sm:text-[48px] sm:leading-[52px]">
        {t("customerSelect.title")}
      </h1>

      <p className="sm:mb-8 mb-6 text-[16px] leading-[22px] text-muted-foreground sm:text-lg sm:leading-6">
        {t("customerSelect.description")}
      </p>

      <form onSubmit={handleConfirm} className="flex w-full sm:max-w-[541px] max-w-full flex-col gap-4 sm:gap-5">
        <RadioGroup
          value={selectedId}
          onValueChange={(value) => {
            setSelectedId(value)
            setSubmitError(null)
          }}
          className="flex flex-col gap-2"
        >
          {customers.map((customer) => {
            const itemId = `customer-${customer.customerId}`

            return (
              <div key={customer.customerId}>
                <RadioGroupItem id={itemId} value={String(customer.customerId)} className="peer sr-only" />
                <Label
                  htmlFor={itemId}
                  className={cn(
                    "flex sm:min-h-[92px] min-h-[80px] cursor-pointer flex-col items-start justify-center rounded-[4px] border-2 border-transparent bg-input p-3",
                    "hover:border-brand hover:bg-card",
                    "peer-data-[state=checked]:pointer-events-none peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-card",
                  )}
                >
                  <CustomerInfo
                    customerType={customer.customerType}
                    name={customer.name}
                    email={customer.email}
                    phone={customer.phone}
                  />
                </Label>
              </div>
            )
          })}
        </RadioGroup>

        {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

        <Button
          type="submit"
          disabled={!selectedId || isSubmitting || isLoggingOut}
          endIcon={<ArrowRightIcon size={24} />}
          className="h-[48px] text-[16px] leading-[24px] sm:h-[60px] sm:text-[18px]"
        >
          {isSubmitting ? "Загружаем..." : t("customerSelect.confirm")}
        </Button>
      </form>
    </>
  )
}
